import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "locales";
import useAuthUser from "hooks/useAuthUser";
import useBaseForm from "hooks/useBaseForm";
import useLiveChatName from "hooks/useLiveChatName";
import { validateFields } from "lib/validation";
import BaseForm from "components/BaseForm";
import JoinLiveChatModal from "components/JoinLiveChatModal";
import providers from "lib/providers";

function GoConferenceModal({ onClose, conferenceUuid }: { onClose: () => void; conferenceUuid: string }) {
  const { user } = useAuthUser();
  const { liveChatName, updateLiveChatName } = useLiveChatName(user?.screenName ?? "");
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);

  const { fields, values, errors, setValues, setErrors } = useBaseForm(
    [
      {
        name: "chat-name",
        type: "text",
        label: t("Your name"),
        validation: {
          required: true,
        },
      },
    ],
    {
      "chat-name": liveChatName,
    }
  );

  const onSubmit = async () => {
    const validation = validateFields(fields, values);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }
    const screenName = values["chat-name"];
    updateLiveChatName(screenName);

    if (!user) {
      // Guest flow: verify the conference is in a public group
      setSubmitting(true);
      try {
        await providers.GroupsProvider.guestJoinConference(conferenceUuid, screenName);
      } catch (err: any) {
        const msg = err?.graphQLErrors?.[0]?.message || err?.message || "";
        const userMsg = msg.includes("not active")
          ? t("This conference has ended")
          : msg.includes("not found")
            ? t("Conference not found")
            : t("This conference is not available for guest access");
        setErrors({ "chat-name": userMsg });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    history.push(`/conference/${conferenceUuid}`);
  };

  useEffect(() => {
    if (!conferenceUuid) onClose();
  }, [conferenceUuid, onClose]);

  if (!conferenceUuid) return null;

  return (
    <JoinLiveChatModal
      className={"go-conference-chat-modal"}
      onClose={onClose}
      onSubmit={onSubmit}
      screenName={values["chat-name"]}
    >
      <BaseForm
        className="divider-bottom__solid pb-4 mb-4"
        fields={fields}
        values={values}
        onChange={setValues}
        setErrors={setErrors}
        errors={errors}
      />
      {submitting && <div className="text-center text-muted">{t("Verifying access...")}</div>}
    </JoinLiveChatModal>
  );
}

export default GoConferenceModal;
